import {
  authApiClient,
  axApiClient,
  cxApiClient,
  dataMigrationApiClient,
  mxApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import {
  DataHistoryResponse,
  historySummaryResponse,
  RelationTabsResponse,
  GroupedDataResponse,
  NullRowsResponse,
  AssignRelationPayload,
  ValidationSummary,
  UploadDataFileResponse,
  DataProgressResponse,
  UploadPreviewResponse,
  FuzzyMatchResponse,
  GetSuggestionsResponse,
  UpdateFuzzyMappingResponse,
  ApplyTransformationResponse,
  RemoveTransformationResponse,
  TransformationPreviewResponse,
  SaveAsTemplateResponse,
  GetSavedTemplatesResponse,
  TemplateDetailResponse,
  FinalizeStageResponse,
  AssignRelationResponse,
  ValidationErrorsResponse,
  ApplyFormatFixesResponse,
  OperationalStatusOption,
} from "./types";
import { ApiEndpoints } from "@shared/api/api-endpoints";
import { toSnakeCase } from "@shared/utils/caseConverter";
import { DEFAULT_PAGE_NUMBER, PAGE_SIZE } from "@shared/utils/constants";
export const onboardingDataMigrationApi = {
  getMigrationModules: async (remoteUtilityId: string): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "dataMigration",
      "migrations/choices/migrationModule",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      }
    );
    const response = await dataMigrationApiClient.get(url);
    return response.data;
  },
  getDataTypesByModule: async (
    moduleKey: number,
    remoteUtilityId: string
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "dataMigration",
      `migrations/data-types/${moduleKey}`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      }
    );
    const response = await dataMigrationApiClient.get(url);
    return response.data;
  },

  // ✅ Get updation data types
  getUpdationDataTypes: async (
    categoryKey: string,
    remoteUtilityId: string
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "dataMigration",
      "migrations/upload-columns",
      (query) => {
        query.push("data_type", categoryKey);
        query.push("remote_utility_id", remoteUtilityId);
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result ?? response.data.dataType.result ?? [];
  },

  getMigrationHistorySummary: async (
    remoteUtilityId: string
  ): Promise<historySummaryResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "dataMigration",
      "migrations/dashboard",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      }
    );

    const response = await dataMigrationApiClient.get(url);
    return response.data.result;
  },

  getDataHistory: async (
    utilityId: string,
    page?: number,
    limit?: number,
    search?: string
  ): Promise<DataHistoryResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "dataMigration",
      "migrations/history",
      (query) => {
        query.push("remote_utility_id", utilityId);

        if (page) {
          query.push("page", page);
        }

        if (limit) {
          query.push("limit", limit);
        }

        if (search) {
          query.push("search", search);
        }
      }
    );
    const response = await dataMigrationApiClient.get(url);
    return response.data;
  },
};

export const getDownloadTemplate = async (
  dataType: number,
  remoteUtilityId: string,
  additionalParams?: { is_update?: number; selected_fields?: string[] }
): Promise<Blob> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    "migrations/download-template",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("data_type", dataType);

      if (additionalParams?.is_update) {
        query.push("is_update", additionalParams.is_update);
      }
      if (additionalParams?.selected_fields?.length) {
        for (const field of additionalParams.selected_fields) {
          query.push("fields", field);
        }
      }
    }
  );
  const response = await dataMigrationApiClient.get(url, {
    responseType: "blob",
  });
  return response.data;
};

export const downloadErrorReport = async (
  taskID: string,
  dataUploadId: string
): Promise<Blob> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    "migrations/download-report",
    (query) => {
      query.push("task_id", taskID);
      query.push("data_upload_id", dataUploadId);
    }
  );

  const response = await onboardingApiClient.get(url, {
    responseType: "blob", // 👈 to handle file download
  });

  return response.data;
};

export const uploadDataFile = async (
  file: File,
  migrationName: string,
  options: {
    dataTypeName?: string;
    remoteUtilityId?: string;
    operationType?: string;
    dataType?: string;
    templateId?: string;
  } = {}
): Promise<UploadDataFileResponse> => {
  const { dataTypeName, remoteUtilityId, operationType, dataType, templateId } =
    options;

  const formData = new FormData();
  formData.append("file", file);
  formData.append("migration_name", migrationName);
  formData.append("data_type", dataType);
  formData.append("operation_type", operationType);
  formData.append("remote_utility_id", remoteUtilityId);
  // formData.append("operation_type", operationType);

  if (templateId) {
    formData.append("template_id", templateId);
  }

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    "migrations/upload"
  );
  const response = await dataMigrationApiClient.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
};

// Get data progress for polling
export const getDataProgress = async (
  sessionId: string
): Promise<DataProgressResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/progress`
  );

  const response = await dataMigrationApiClient.get(url);
  return response.data;
};

export const getUploadPreview = async (
  sessionId: string
): Promise<UploadPreviewResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/upload-result`
  );

  const response = await dataMigrationApiClient.get(url);
  return response.data.result;
};

// Match columns as soon as file is uploaded
export const postColumnsMatching = async (payload: {
  data_type_name: string;
  operation_type: number;
  data_upload_id: number;
  remote_utility_id: number;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "data-upload/columns-matching"
  );
  const response = await onboardingApiClient.post(url, payload);
  return response.data;
};

export const getTargetMappingOptions = async (
  dataType: string,
  operationType: number
): Promise<string[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "data-upload/columns",
    (query) => {
      query.push("data_type_name", dataType);
      query.push("operation_type", String(operationType));
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result;
};

// src/api/index.ts or your api file
export const updateColumnMapping = async (uploadId, payload) => {
  const url = `/columns-matching-update/${uploadId}/`;
  return onboardingApiClient.put(url, payload); // Adjust onboardingApiClient based on your client setup
};

// Match columns as soon as file is uploaded
export const validateData = async (payload: {
  data_upload_id: number;
  remote_utility_id: number;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "validate-upload"
  );
  const response = await onboardingApiClient.post(url, payload);
  return response.data;
};

export const standardizeIssue = async (data_upload_id, issue, utilityId) => {
  const payload = {
    error_type: [issue.errorType],
    remote_utility_id: utilityId,
  };

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    `fix-data-issues/${data_upload_id}`
  );

  const response = await onboardingApiClient.put(url, payload);
  return response.data;
};

export const autoGenerateIssue = async (data_upload_id, issue, utilityId) => {
  const payload = {
    error_type: [issue.errorType],
    remote_utility_id: utilityId,
    is_num_format: 1,
  };

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    `fix-data-issues/${data_upload_id}`
  );

  const response = await onboardingApiClient.put(url, payload);
  return response.data;
};

// Get data for plans
export const getUtilityPlans = async (
  remoteUtilityId: string
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "plan-utility-wise",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data;
};

// Get data for premise
export const getUtilityPremise = async (
  remoteUtilityId: string
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/territory_json",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("config_level", "premise");
      query.push("is_pagination_required", "false");
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data;
};

// Get missing field data
export const getMissingFieldsData = async (
  uploadId: number,
  fieldName: string,
  remoteUtilityId: string
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "missing-field-data",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("field_name", fieldName);
      query.push("data_upload_id", uploadId);
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data;
};

// assign plan to selected data
export const assignPlan = async (payload): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "update-missing-data"
  );
  const response = await onboardingApiClient.post(url, payload);
  return response.data;
};
export const assignServiceArea = async (payload): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "update-missing-data"
  );
  const response = await onboardingApiClient.post(url, payload);
  return response.data;
};

// Updated API function to match your requirements
export const fixDataIssue = async (data_upload_id, payload) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    `fix-data-issues/${data_upload_id}`
  );
  const response = await onboardingApiClient.put(url, payload);
  return response.data;
};

// Get Trasnfromed data
export const getTransFormData = async (
  uploadId: number,
  remoteUtilityId: string
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "transformed_data",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("data_upload_id", uploadId);
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data;
};

// Match columns as soon as file is uploaded
export const startMigration = async (sessionId: string): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/start-final-migration`
  );
  const response = await dataMigrationApiClient.post(url);
  return response.data;
};

export const getErrorLogs = async (
  sessionId: string,
  taskID: string
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/migration-result`,
    (query) => {
      query.push("task_id", taskID);
    }
  );

  const response = await dataMigrationApiClient.get(url);
  return response.data;
};

export const postFuzzyMatch = async (
  sessionId: string,
  dataType: number,
  operationType?: number,
  selectedFields?: string[]
): Promise<FuzzyMatchResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/fuzzy-match`
  );

  // Build payload dynamically
  const payload: any = {
    dataType: dataType,
  };

  // Add operation_type if provided
  if (operationType !== undefined) {
    payload.operation_type = operationType;
  }

  // Add fields only if operation_type is 1 and selectedFields exist
  if (operationType === 1 && selectedFields && selectedFields.length > 0) {
    payload.fields = selectedFields;
  }

  const response = await dataMigrationApiClient.post(url, payload);
  return {
    sessionId: response.data.sessionId,
    message: response.data.message,
    taskId: response.data.taskId,
  };
};

export const getSuggestions = async (
  sessionId: string
): Promise<GetSuggestionsResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/suggestions`
  );

  const response = await dataMigrationApiClient.get(url);

  return {
    sessionId: response.data.sessionId,
    suggestions: response.data.suggestions,
  };
};

export const updateFuzzyMapping = async (
  sessionId: string,
  payload: {
    targetField: string;
    sourceColumn: string;
    confidence: number;
  }
): Promise<UpdateFuzzyMappingResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/update-mapping`
  );

  const response = await dataMigrationApiClient.post(url, {
    targetField: toSnakeCase(payload.targetField),
    sourceColumn: payload.sourceColumn,
    confidence: payload.confidence,
  });

  return {
    sessionId: response.data.result.sessionId,
    message: response.data.result.message,
    targetField: response.data.result.targetField,
    sourceColumn: response.data.result.sourceColumn,
  };
};

export const applyTransformation = async (
  sessionId: string,
  payload: {
    targetField: string;
    transformation: {
      type: string;
      order: number;
      config: Record<string, any>;
    };
  }
): Promise<ApplyTransformationResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/apply-transformation`
  );
  const response = await dataMigrationApiClient.post(url, {
    targetField: payload.targetField,
    transformation: payload.transformation,
  });

  return {
    sessionId: response.data.result.sessionId,
    message: response.data.result.message,
    taskId: response.data.result.taskId,
  };
};

export const removeTransformation = async (
  sessionId: string,
  payload: {
    targetField: string;
    order: number;
  }
): Promise<RemoveTransformationResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/remove-transformation`
  );
  const response = await dataMigrationApiClient.post(url, {
    targetField: payload.targetField,
    order: payload.order,
  });

  return {
    sessionId: response.data.result.sessionId,
    message: response.data.result.message,
    taskId: response.data.result.taskId,
  };
};

export const getTransformationPreview = async (
  sessionId: string,
  sourceColumn: string
): Promise<TransformationPreviewResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/transformation-preview`,
    (query) => {
      query.push("source_column", sourceColumn);
    }
  );

  const response = await dataMigrationApiClient.get(url);

  return {
    sessionId: response.data.sessionId,
    preview: response.data.preview,
  };
};

export const saveAsTemplate = async (
  sessionId: string,
  templateName: string,
  description?: string
): Promise<SaveAsTemplateResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/save-as-template`
  );

  const response = await dataMigrationApiClient.post(url, {
    template_name: templateName,
    description: description || `Template created from ${templateName}`,
  });

  return response.data.result;
};
// Add this to your api.ts file (document 10)

export const getSavedTemplates = async (
  page: number = 1,
  limit: number = 10,
  search?: string,
  remoteUtilityId?: string
): Promise<GetSavedTemplatesResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    "migrations/template-list",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("page", page);
      query.push("limit", limit);
      if (search) {
        query.push("search", search);
      }
    }
  );

  const response = await dataMigrationApiClient.get(url);
  return response.data;
};

export const getTemplateDetail = async (
  templateId: string,
  remoteUtilityId: string
): Promise<TemplateDetailResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/template-detail-view/${templateId}`,
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    }
  );

  const response = await dataMigrationApiClient.get(url);
  return response.data.result;
};

export const finalizeStage2 = async (
  sessionId: string
): Promise<FinalizeStageResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/finalize-stage2`
  );

  const response = await dataMigrationApiClient.post(url);

  return {
    sessionId: response.data.result.session_id,
    message: response.data.result.message,
    nextStage: response.data.result.next_stage,
    taskId: response.data.result.task_id,
  };
};

export const finalizeStage3 = async (
  sessionId: string,
  payload: { confirmed: boolean }
): Promise<FinalizeStageResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/finalize-stage3`
  );

  try {
    const response = await dataMigrationApiClient.post(url, payload);

    // Extract data from response
    const responseData = response.data;
    const result = responseData?.result;

    // Build return object with fallbacks
    return {
      sessionId:
        result?.session_id ||
        result?.sessionId ||
        responseData?.session_id ||
        responseData?.sessionId ||
        sessionId,
      message:
        result?.message ||
        responseData?.message ||
        "Stage 3 finalized successfully",
      nextStage:
        result?.next_stage ||
        result?.nextStage ||
        responseData?.next_stage ||
        responseData?.nextStage ||
        "stage_4",
      taskId:
        result?.task_id ||
        result?.taskId ||
        responseData?.task_id ||
        responseData?.taskId ||
        "",
    };
  } catch (error) {}
};

export const getRelationTabs = async (
  sessionId: string
): Promise<RelationTabsResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/relation-tabs`
  );
  const response = await dataMigrationApiClient.get(url);
  return response.data.result;
};

export const getGroupedRelationData = async (
  sessionId: string,
  tabName: string,
  filters?: Record<string, any>
): Promise<GroupedDataResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/relation-data/${tabName}`,
    (query) => {
      if (filters) {
        // Search
        if (filters.search) {
          query.push("search", filters.search);
        }

        // Pagination for grouped data (if backend supports)
        if (filters.page) {
          query.push("page", filters.page);
        }

        if (filters.limit) {
          query.push("limit", filters.limit);
        }

        // Status filter (mapped/unmapped)
        if (filters.status && filters.status !== "all") {
          query.push("status", filters.status);
        }
      }
    }
  );
  const response = await dataMigrationApiClient.get(url);
  return response.data;
};

export const getNullRowsData = async (
  sessionId: string,
  tabName: string,
  page: number = DEFAULT_PAGE_NUMBER,
  pageSize: number = PAGE_SIZE,
  filters?: Record<string, any>
): Promise<NullRowsResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/relation-null-rows/${tabName}`,
    (query) => {
      query.push("page", page);
      query.push("page_size", pageSize);

      if (filters) {
        if (filters.search) {
          query.push("search", filters.search);
        }

        if (filters.assignmentStatus) {
          query.push("assignment_status", filters.assignmentStatus);
        }

        Object.entries(filters).forEach(([key, value]) => {
          // Skip empty values
          if (
            value === null ||
            value === undefined ||
            value === "" ||
            value === "all"
          ) {
            return;
          }

          if (Array.isArray(value)) {
            if (value.length > 0) {
              query.push(key, value.join(","));
            }
          } else if (value instanceof Date) {
            query.push(key, value);
          } else if (typeof value === "object" && (value.min || value.max)) {
            if (value.min) {
              query.push(`${key}_min`, value.min);
            }
            if (value.max) {
              query.push(`${key}_max`, value.max);
            }
          } else {
            query.push(key, String(value));
          }
        });
      }
    }
  );

  const response = await dataMigrationApiClient.get(url);
  return response.data;
};

export const assignRelation = async (
  sessionId: string,
  payload: AssignRelationPayload
): Promise<AssignRelationResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/assign-relation`
  );
  const response = await dataMigrationApiClient.post(url, payload);
  return response.data;
};

// Get category/subcategory options
export const getCategoryOptions = async (
  remoteUtilityId: string,
  configLevel: string
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/category_json",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("config_level", configLevel);
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result;
};

// Get plan options
export const getPlanOptions = async (remoteUtilityId: string): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "plan",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("is_draft", 1);
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result;
};
export const finalizeStage4 = async (
  sessionId: string,
  payload: {
    confirmed: boolean;
    proceedWithErrors: boolean;
  }
): Promise<FinalizeStageResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/finalize-stage4`
  );

  const response = await dataMigrationApiClient.post(url, payload);

  return {
    sessionId: response.data.result.sessionId,
    message: response.data.result.message,
    nextStage: response.data.result.nextStage,
    taskId: response.data.result.taskId,
  };
};

export const getValidationErrors = async (
  sessionId: string
): Promise<ValidationErrorsResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/validation-errors`
  );
  const response = await dataMigrationApiClient.get(url);
  return response.data; // ← Return the whole response as-is
};

export const getErrorDetail = async (
  sessionId: string,
  errorType: string,
  field: string
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/error-detail`,
    (query) => {
      query.push("error_type", errorType);
      query.push("field", field);
    }
  );
  const response = await dataMigrationApiClient.get(url);
  return response.data;
};

export const applyFormatFixes = async (
  sessionId: string
): Promise<ApplyFormatFixesResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${sessionId}/apply-format-fixes`
  );
  const response = await dataMigrationApiClient.post(url, {
    auto_fix: true,
  });
  return response.data.result;
};
export const getConsumerStatusOptions = async (
  remoteUtilityId: string
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "choices/consumer_status",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    }
  );

  const response = await cxApiClient.get(url);
  return response.data.result;
};

export const getSystemStatusOptions = async (
  remoteUtilityId: string
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    "choices/system_status",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    }
  );

  const response = await axApiClient.get(url);
  return response.data.result;
};

export const getAssetStatusOptions = async (
  remoteUtilityId: string
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    "choices/asset_status",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    }
  );

  const response = await axApiClient.get(url);
  return response.data.result;
};

export const getMeterTypeOptions = async (): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "choices/meter_type"
  );
  const response = await mxApiClient.get(url);
  return response.data.result;
};

export const getMeterManufacturerOptions = async (
  remoteUtilityId: string
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter/manufacturer",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("is_dropdown", "1");
    }
  );
  const response = await mxApiClient.get(url);
  return response.data.result;
};

export const getMeterModelOptions = async (
  remoteUtilityId: string
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-model",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    }
  );
  const response = await mxApiClient.get(url);
  return response.data.result;
};

export const getUtilityServicesOptions = async (
  utilityId: string
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "auth",
    "utility/services",
    (query) => {
      query.push("utility_id", utilityId);
    }
  );
  const response = await authApiClient.get(url); // Use appropriate client
  return response.data.result;
};

export const getValidationSummary = async (
  uploadId: string
): Promise<ValidationSummary> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/${uploadId}/validation-summary`
  );

  const response = await dataMigrationApiClient.get(url);

  return response.data.result;
};

export const getAssetClassOptions = async (
  remoteUtilityId: string,
  configLevel: string = "asset_class",
  showInactive: boolean = true
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("config_level", configLevel);
      query.push("show_inactive", String(showInactive));
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result;
};

// Asset Type
export const getAssetTypeOptions = async (
  remoteUtilityId: string,
  configLevel: string = "asset_type"
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("config_level", configLevel);
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result;
};

// Manufacturer (from AX API)
export const getManufacturerOptionsAX = async (
  remoteUtilityId: string,
  page: number,
  limit: number
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    "manufacturer",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("page", page);
      query.push("limit", limit);
    }
  );

  const response = await axApiClient.get(url);
  return response.data.results;
};

// Depreciation Method
export const getDepreciationMethodOptions = async (): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    "choices/depreciation_method"
  );
  const response = await axApiClient.get(url);
  return response.data.result;
};

// Network Type
export const getNetworkTypeOptions = async (): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    "choices/network_type"
  );
  const response = await axApiClient.get(url);
  return response.data.result;
};

// Facility Status / Operation Status
export const getFacilityStatusOptions = async (): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    "choices/facility_status"
  );
  const response = await axApiClient.get(url);
  return response.data.result;
};

// Backup Status
export const getBackupStatusOptions = async (): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    "choices/backup_status"
  );
  const response = await axApiClient.get(url);
  return response.data.result;
};

// System Type
export const getSystemTypeOptions = async (
  remoteUtilityId: string,
  configLevel: string = "system_type",
  showInactive: boolean = true
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/system_json",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("config_level", configLevel);
      query.push("show_inactive", String(showInactive));
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result;
};

// Operational Hours
export const getOperationalHoursOptions = async (): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    "choices/operational_hours"
  );
  const response = await axApiClient.get(url);
  return response.data.result;
};

export const getOperationalStatusOptions =
  async (): Promise<OperationalStatusOption> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "choices/operational_status"
    );
    const response = await axApiClient.get(url);
    return response.data.result;
  };

// Facility Type
export const getFacilityTypeOptions = async (
  remoteUtilityId: string,
  configLevel: string = "facility_type",
  showInactive: boolean = true
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/facility_type",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("config_level", configLevel);
      query.push("show_inactive", String(showInactive));
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result;
};

// Utility Services (AX version)
export const getUtilityServicesAXOptions = async (
  remoteUtilityId: string
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "auth",
    "utility/services",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    }
  );

  const response = await authApiClient.get(url);
  return response.data.result;
};

// Service Zone/Division (Territory)
export const getTerritoryZoneOptions = async (
  remoteUtilityId: string,
  configLevel: string = "zone"
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/territory_json",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("config_level", configLevel);
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result;
};

// Depreciation Schedule
export const getDepreciationScheduleOptions = async (): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    "choices/depreciation_schedule"
  );
  const response = await axApiClient.get(url);
  return response.data.result;
};

export const getPremiseOptions = async (
  remoteUtilityId: string,
  showInactive: boolean = true
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/territory_json",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("config_level", "premise");
      query.push("show_inactive", String(showInactive));
      query.push("is_pagination_required", "false");
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data;
};

export const deleteMigrationTemplate = async (
  templateId: string | number
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "dataMigration",
    `migrations/template-detail-view/${templateId}`
  );

  const response = await dataMigrationApiClient.delete(url);
  return response.data;
};
